/**
 * Pure state transition for a completed trade. Extracted from
 * useAlbum.completeTrade so we can unit-test the math without a React
 * renderer, Firestore, or async plumbing.
 *
 * Inputs:
 *   prevState       — { collected, duplicates, trades, friends, history }
 *   receiveNumbers  — sticker numbers the user just received
 *   giveNumbers     — sticker numbers the user just handed over
 *   friendUid       — counterparty (used to delete the trade entry and
 *                     tag the history row); may be falsy to skip
 *
 * Output:
 *   { nextState, finalDuplicates, historyEntry }
 *   where nextState is a shallow-cloned mutation safe to feed into
 *   React setState / Firestore updateDoc.
 */
// Normalize: strip whitespace + uppercase. Permite que un giveNumber
// "MEX 5" coincida con una key historica "MEX5" en state.duplicates
// (o viceversa). Si los keys son consistentes, esto es no-op.
const normNum = (v) => String(v ?? '').replace(/\s+/g, '').toUpperCase();

export function applyCompletedTrade(prevState, receiveNumbers, giveNumbers, friendUid, friendNameOverride) {
  const collected = { ...(prevState.collected || {}) };
  const duplicates = { ...(prevState.duplicates || {}) };
  const trades = { ...(prevState.trades || {}) };
  const history = [...(prevState.history || [])];

  // Mapeo normalizado para tolerar inconsistencias de whitespace en
  // las keys de duplicates. Algunos registros legacy del usuario
  // tenían keys sin espacio ("MEX5") mientras que los stickers usan
  // la forma canónica con espacio ("MEX 5"). Sin este mapa, el
  // decremento fallaba silenciosamente — "no me está descontando".
  const dupKeyByNorm = {};
  Object.keys(duplicates).forEach((k) => {
    dupKeyByNorm[normNum(k)] = k;
  });

  receiveNumbers.forEach((num) => {
    collected[num] = true;
  });

  giveNumbers.forEach((num) => {
    // Primero busca la key exacta; si no existe, intenta el match
    // normalizado. Ambas estrategias actualizan SIEMPRE la entrada
    // existente para no duplicar keys.
    const exactKey = duplicates[num] !== undefined ? num : null;
    const normKey = exactKey || dupKeyByNorm[normNum(num)];
    if (!normKey) return; // no tenía duplicates de esta lámina — nada que descontar
    const current = duplicates[normKey] || 0;
    if (current > 1) duplicates[normKey] = current - 1;
    else delete duplicates[normKey];
  });

  if (friendUid) delete trades[friendUid];

  // friendNameOverride toma precedencia. Lo usamos en el flujo de
  // "Intercambio presencial → Cerrar trato" donde el amigo no se llega
  // a crear en state.friends (sería ephemero — completeTrade lo borra
  // inmediatamente) pero igual queremos que el history quede etiquetado
  // con el nombre que el usuario escribió en lugar del genérico
  // "Amigo Externo".
  const friendName =
    friendNameOverride ||
    prevState.friends?.[friendUid]?.name ||
    (friendUid?.startsWith('ext-') ? 'Amigo Externo' : 'Coleccionista');

  const historyEntry = {
    id: Date.now(),
    type: 'trade',
    friendUid: friendUid || null,  // ← nuevo: permite trust-score por amigo (entries
                                    //   viejos no lo tienen → trust-score los ignora)
    friendName,
    receive: receiveNumbers.length,
    give: giveNumbers.length,
    date: new Date().toISOString(),
  };
  history.unshift(historyEntry);

  const nextState = {
    ...prevState,
    collected,
    duplicates,
    trades,
    history: history.slice(0, 10),
  };

  return { nextState, finalDuplicates: duplicates, historyEntry };
}
